import { Box, Button, Divider } from '@atgfr8/atg-ui-tailwind';
import { tryGetRoute } from '@components/Sidebar/helpers';
import { A } from '@solidjs/router';
import { debounce, size } from 'lodash';
import { For, Show, createSignal, onCleanup, onMount } from 'solid-js';

import { NavLink } from './NavLink';

type MainMenuItemProps = {
  navlink: NavLink;
};

const [activeMenuId, setActiveMenuId] = createSignal<string | null>(null);

export default function MainMenuItem(props: MainMenuItemProps) {
  const [anchorEl, setAnchorEl] = createSignal<HTMLButtonElement>();
  // This menu is open if it's the active menu and has an anchor element
  const isOpen = () =>
    activeMenuId() === props.navlink.id && Boolean(anchorEl());

  const handleClose = () => {
    if (activeMenuId() === props.navlink.id) {
      setActiveMenuId(null);
    }
  };

  const handleChildClick = (custom?: () => void) => {
    handleClose();
    custom?.();
  };

  const handleMenuClick = debounce(() => {
    if (isOpen()) {
      handleClose();
    } else {
      setActiveMenuId(props.navlink.id);
    }
  }, 50);

  const handleClickOutside = (event: MouseEvent) => {
    if (!isOpen()) {
      return;
    }

    const target = event.target as Node;
    const menuElement = anchorEl();

    if (menuElement && !menuElement.contains(target)) {
      handleClose();
    }
  };

  onMount(() => {
    document.addEventListener('click', handleClickOutside, true);
  });

  onCleanup(() => {
    document.removeEventListener('click', handleClickOutside, true);
  });

  return (
    <>
      {!size(props.navlink.children ?? []) ? (
        <Button
          href={
            Boolean(props.navlink.custom)
              ? undefined
              : tryGetRoute(props.navlink.path).route
          }
          id={props.navlink.id}
          aria-controls={isOpen() ? props.navlink.id : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen() ? 'true' : undefined}
          ref={setAnchorEl}
          onClick={() => {
            if (props.navlink.custom) {
              props.navlink.custom();
            } else {
              handleMenuClick(); //event);
            }
          }}
          class="text-[#A5C6DA]! text-[14px]! bg-transparent! hover:bg-transparent! focus:bg-transparent! shadow-none! font-semibold!"
          dataTestId={`main-menu-item-${props.navlink.id}`}
        >
          {props.navlink.label}
        </Button>
      ) : (
        <>
          <Button
            id={props.navlink.id}
            onClick={handleMenuClick}
            class="text-[#A5C6DA]! text-[14px]! relative! bg-transparent! hover:bg-transparent! focus:bg-transparent! shadow-none! font-semibold!"
            style={{ overflow: 'inherit' }}
            ref={setAnchorEl}
            disableRipple
            dataTestId={`main-menu-item-${props.navlink.id}-menu`}
          >
            {props.navlink.label}
            <Show when={isOpen()}>
              <Box class="absolute top-[35px] bg-white z-53 left-[-2px] text-[#000000DE] py-1 rounded-md shadow-[0px_4px_10px_rgba(0,0,0,0.47)] text-left">
                <For
                  each={props.navlink.children?.filter(
                    (child) => !child.hide?.(),
                  )}
                >
                  {(child) => {
                    const route = tryGetRoute(child.path);
                    return (
                      <>
                        <div
                          class="min-w-[170px] whitespace-nowrap text-[#000000DE] hover:bg-[#0e2c3b] hover:text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChildClick(child.custom);
                          }}
                        >
                          {child.icon !== undefined && (
                            <div class="min-w-[36px] flex items-center justify-center text-inherit">
                              {child.icon}
                            </div>
                          )}
                          {/*
                      hold up brave adventurer, the following code is intentional
                      Mailto links are not supported by the A component which is a special component used by solidjs router to handle CSR requests
                      So, we need to ensure that we use the html element a instead for mailto links
                      Please make sure any styling changes apply to both the A component and the a element
                      Hopefully, solidjs will add mailto support to the A component in the future but for now, this is the workaround
                      see https://github.com/solidjs/solid-router/discussions/284 for more information
                      */}
                          {child.mailto !== undefined && child.mailto ? (
                            <a
                              class="capitalize block w-full whitespace-nowrap text-[16px] font-normal px-[16px] py-[6px]"
                              target={'_blank'}
                              href={child.path}
                              onClick={(e) => {
                                handleChildClick(child.custom);
                                e.stopPropagation();
                              }}
                            >
                              {child.label}
                            </a>
                          ) : Boolean(child.externalLink) || route.isV3 ? (
                            <A
                              href={
                                Boolean(child.externalLink)
                                  ? child.path
                                  : route.route
                              }
                              target={
                                Boolean(child.externalLink) || route.isV3
                                  ? '_blank'
                                  : undefined
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                handleChildClick(child.custom);
                                window.open(route.route, '_blank');
                                e.stopPropagation();
                              }}
                              class="capitalize block w-full whitespace-nowrap text-[16px] font-normal px-[16px] py-[6px]"
                            >
                              {child.label}
                            </A>
                          ) : (
                            <A
                              href={
                                Boolean(child.externalLink)
                                  ? child.path
                                  : route.route
                              }
                              target={
                                Boolean(child.externalLink)
                                  ? '_blank'
                                  : undefined
                              }
                              class="capitalize block w-full whitespace-nowrap text-[16px] font-normal px-[16px] py-[6px]"
                              onClick={(e) => {
                                handleChildClick(child.custom);
                                e.stopPropagation();
                              }}
                            >
                              {child.label}
                            </A>
                          )}
                        </div>
                        {child.seperator === true ? (
                          <Divider class="my-2!" />
                        ) : null}
                      </>
                    );
                  }}
                </For>
              </Box>
            </Show>
          </Button>
        </>
      )}
    </>
  );
}
